import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Send, Mail, MessageCircle, Calendar, Users, History, Eye, ArrowRight, Zap,
} from "lucide-react";

const CHANNELS = [
  { v: "email", label: "Email", icon: Mail },
  { v: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { v: "both", label: "Email + WhatsApp", icon: Send },
];

const AUDIENCE_LABELS = {
  all_delegates: "All delegates",
  by_committee: "Selected committee",
  by_country: "Selected country",
  parents: "Parents",
  school_coordinators: "School coordinators",
  chairs: "Chairs / EB",
  volunteers: "Volunteers",
  sponsors: "Sponsors",
  partner_institutions: "Partner institutions",
  pending_payment: "Pending payment users",
  certificate_eligible: "Certificate-eligible users",
};

export default function AutomationCenterPage() {
  const [defs, setDefs] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [form, setForm] = useState({
    channel: "email",
    audience: "all_delegates",
    audience_filter: "",
    custom_subject: "",
    custom_body: "",
    schedule_at: "",
    event_id: "",
  });

  useEffect(() => {
    api.get("/broadcast/templates").then((r) => setDefs(r.data));
    api.get("/broadcast/history").then((r) => setHistory(r.data || []));
    api.get("/broadcast/status").then((r) => setProviderStatus(r.data || null)).catch(() => {});
  }, []);

  const startCompose = (tpl) => {
    setActiveTemplate(tpl);
    setForm({
      channel: "email",
      audience: "all_delegates",
      audience_filter: "",
      custom_subject: tpl.subject,
      custom_body: tpl.body,
      schedule_at: "",
      event_id: "",
    });
  };

  const send = async () => {
    if (!activeTemplate) return;
    try {
      const { data } = await api.post("/broadcast/send", {
        template_key: activeTemplate.key,
        ...form,
      });
      const live = data.delivery_modes && Object.values(data.delivery_modes).some((m) => m === "live");
      toast.success(form.schedule_at
        ? `Scheduled · ${data.recipient_count} recipients`
        : live
          ? `Sent (LIVE) · ${data.delivered_count}/${data.recipient_count} delivered`
          : `Sent (mock) · ${data.recipient_count} recipients`);
      const r = await api.get("/broadcast/history");
      setHistory(r.data || []);
      setActiveTemplate(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Automation Center</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            One <span className="font-serif italic text-[#0F4C81]">command center</span> for every conference message.
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Email, WhatsApp, scheduled or instant. Personalisation tokens like
            <span className="font-mono mx-1">{`{{delegate_name}}`}</span> auto-fill from event data.
          </p>
          {providerStatus && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="provider-status">
              <ProviderPill label="Email" live={providerStatus.email?.live} />
              <ProviderPill label="WhatsApp" live={providerStatus.whatsapp?.live} />
              {(!providerStatus.email?.live || !providerStatus.whatsapp?.live) && (
                <span className="text-[10px] text-slate-500 ml-1">
                  Add API keys in <span className="font-mono">backend/.env</span> to switch from mock to live delivery.
                </span>
              )}
            </div>
          )}
        </div>

        {/* Templates grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {defs?.templates.map((tpl) => (
            <div key={tpl.key} className="rounded-xl bg-white border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`template-card-${tpl.key}`}>
              <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </div>
              <h3 className="mt-4 text-base font-semibold tracking-tight text-[#0A192F]">{tpl.label}</h3>
              <p className="mt-2 text-xs text-slate-500 font-mono line-clamp-2">{tpl.subject}</p>
              <Button
                onClick={() => startCompose(tpl)}
                size="sm"
                variant="outline"
                className="mt-4 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white"
                data-testid={`compose-${tpl.key}`}
              >
                Compose <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          ))}
        </div>

        {/* Compose modal */}
        {activeTemplate && (
          <Dialog open onOpenChange={() => setActiveTemplate(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-testid="compose-title">Compose · {activeTemplate.label}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Channel</Label>
                    <select
                      value={form.channel}
                      onChange={(e) => setForm({ ...form, channel: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                      data-testid="compose-channel"
                    >
                      {CHANNELS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Audience</Label>
                    <select
                      value={form.audience}
                      onChange={(e) => setForm({ ...form, audience: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                      data-testid="compose-audience"
                    >
                      {Object.entries(AUDIENCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
                {(form.audience === "by_committee" || form.audience === "by_country") && (
                  <div>
                    <Label className="text-xs">{form.audience === "by_committee" ? "Committee ID" : "Country name"}</Label>
                    <Input value={form.audience_filter} onChange={(e) => setForm({ ...form, audience_filter: e.target.value })} data-testid="compose-filter" />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={form.custom_subject} onChange={(e) => setForm({ ...form, custom_subject: e.target.value })} data-testid="compose-subject" />
                </div>
                <div>
                  <Label className="text-xs">Body</Label>
                  <Textarea rows={6} value={form.custom_body} onChange={(e) => setForm({ ...form, custom_body: e.target.value })} data-testid="compose-body" />
                  <p className="mt-1 text-[10px] text-slate-500">
                    Tokens: <span className="font-mono">{`{{delegate_name}} {{event_name}} {{committee_name}} {{country_name}} {{room_number}} {{schedule_time}} {{certificate_link}} {{payment_link}}`}</span>
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Schedule (optional)</Label>
                    <Input type="datetime-local" value={form.schedule_at} onChange={(e) => setForm({ ...form, schedule_at: e.target.value })} data-testid="compose-schedule" />
                  </div>
                  <div>
                    <Label className="text-xs">Event ID (optional)</Label>
                    <Input value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} placeholder="event-globex-2026" data-testid="compose-event" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActiveTemplate(null)}>Cancel</Button>
                <Button onClick={send} className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]" data-testid="compose-send">
                  {form.schedule_at ? <><Calendar className="h-3.5 w-3.5 mr-1.5" /> Schedule</> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Mock-send</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* History */}
        <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6" data-testid="broadcast-history">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-[#0F4C81]" />
            <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">Recent broadcasts</h2>
          </div>
          {history.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No broadcasts sent yet.</p>
          ) : (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b">
                  <tr>
                    <th className="py-2.5 font-semibold">Template</th>
                    <th className="py-2.5 font-semibold">Channel</th>
                    <th className="py-2.5 font-semibold">Audience</th>
                    <th className="py-2.5 font-semibold">Recipients</th>
                    <th className="py-2.5 font-semibold">Status</th>
                    <th className="py-2.5 font-semibold">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((b) => (
                    <tr key={b.id}>
                      <td className="py-3 font-semibold text-[#0A192F]">{b.template_label}</td>
                      <td className="py-3 text-slate-600 text-xs uppercase tracking-[0.18em] font-bold">{b.channel}</td>
                      <td className="py-3 text-slate-600 text-xs">{AUDIENCE_LABELS[b.audience] || b.audience}</td>
                      <td className="py-3 text-slate-700 font-mono text-xs">{b.recipient_count}</td>
                      <td className="py-3"><span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${b.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{b.status}</span></td>
                      <td className="py-3 text-slate-500 text-xs">{b.sent_at ? new Date(b.sent_at).toLocaleString() : (b.scheduled_at ? `Scheduled ${new Date(b.scheduled_at).toLocaleString()}` : "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function ProviderPill({ label, live }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full ${
        live
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
      data-testid={`provider-${label.toLowerCase()}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
      {label}: {live ? "Live" : "Mock"}
    </span>
  );
}
